import React, { useState, useEffect, useContext, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUploadCloud,
  FiGrid,
  FiList,
  FiFolder,
  FiFile,
  FiImage,
  FiCode,
  FiDatabase,
  FiFileText,
  FiRefreshCw,
  FiPackage,
} from "react-icons/fi";
import { useRoomFiles } from "../../../context/RoomFilesContext";
import { UserContext } from "../../../context/UserContext";
import FileCard from "../components/FileCard";
import FileUploadModal from "../components/FileUploadModal";
import FileVersionHistory from "../components/FileVersionHistory";
import "./FilesTab.css";

// ==============================================
// Constants
// ==============================================
const FILE_TYPES = [
  {
    id: "all",
    label: "All",
    icon: FiPackage,
    color: "#6366f1",
    bg: "rgba(99, 102, 241, 0.1)",
  },
  {
    id: "pdf",
    label: "PDFs",
    icon: FiFile,
    color: "#ef4444",
    bg: "rgba(239, 68, 68, 0.1)",
  },
  {
    id: "image",
    label: "Images",
    icon: FiImage,
    color: "#10b981",
    bg: "rgba(16, 185, 129, 0.1)",
  },
  {
    id: "code",
    label: "Code",
    icon: FiCode,
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.1)",
  },
  {
    id: "dataset",
    label: "Data",
    icon: FiDatabase,
    color: "#8b5cf6",
    bg: "rgba(139, 92, 246, 0.1)",
  },
  {
    id: "document",
    label: "Docs",
    icon: FiFileText,
    color: "#3b82f6",
    bg: "rgba(59, 130, 246, 0.1)",
  },
];

// ==============================================
// Animation Variants
// ==============================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
};

// ==============================================
// Sub-Components
// ==============================================

// Header Component
const FilesHeader = ({ fileCount, onRefresh, isRefreshing, onUpload }) => (
  <header className="rfile-header">
    <div className="rfile-header-left">
      <div className="rfile-title-group">
        <h1 className="rfile-title">Files</h1>
        <motion.span
          className="rfile-badge"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, delay: 0.2 }}
        >
          {fileCount}
        </motion.span>
      </div>
      <p className="rfile-subtitle">Manage your research files</p>
    </div>
    <div className="rfile-header-actions">
      <motion.button
        className={`rfile-icon-btn ${
          isRefreshing ? "rfile-icon-btn--spin" : ""
        }`}
        onClick={onRefresh}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title="Refresh"
      >
        <FiRefreshCw size={18} />
      </motion.button>
      <motion.button
        className="rfile-upload-btn"
        onClick={onUpload}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
      >
        <FiUploadCloud size={18} />
        <span>Upload</span>
      </motion.button>
    </div>
  </header>
);

// Filter Tabs Component
const FilterTabs = ({ activeType, onTypeChange, getTypeCount }) => (
  <div className="rfile-filters">
    <div className="rfile-type-tabs">
      {FILE_TYPES.map(({ id, label, icon: Icon, color, bg }) => (
        <motion.button
          key={id}
          className={`rfile-tab ${
            activeType === id ? "rfile-tab--active" : ""
          }`}
          onClick={() => onTypeChange(id)}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
          style={{ "--tab-color": color, "--tab-bg": bg }}
        >
          <Icon size={16} />
          <span className="rfile-tab-label">{label}</span>
          <span className="rfile-tab-count">{getTypeCount(id)}</span>
        </motion.button>
      ))}
    </div>
  </div>
);

// View Toggle Component
const ViewToggle = ({ view, onViewChange }) => (
  <div className="rfile-view-toggle">
    <motion.button
      className={`rfile-view-btn ${
        view === "grid" ? "rfile-view-btn--active" : ""
      }`}
      onClick={() => onViewChange("grid")}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title="Grid View"
    >
      <FiGrid size={16} />
    </motion.button>
    <motion.button
      className={`rfile-view-btn ${
        view === "list" ? "rfile-view-btn--active" : ""
      }`}
      onClick={() => onViewChange("list")}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title="List View"
    >
      <FiList size={16} />
    </motion.button>
  </div>
);

// Empty State Component
const EmptyState = ({ hasFilters, onClearFilters, onUpload }) => (
  <motion.div
    className="rfile-empty"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className="rfile-empty-icon">
      <FiFolder size={48} />
    </div>
    {hasFilters ? (
      <>
        <h3 className="rfile-empty-title">No matching files</h3>
        <p className="rfile-empty-text">Try adjusting your filters</p>
        <motion.button
          className="rfile-empty-btn rfile-empty-btn--secondary"
          onClick={onClearFilters}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Clear Filters
        </motion.button>
      </>
    ) : (
      <>
        <h3 className="rfile-empty-title">No files yet</h3>
        <p className="rfile-empty-text">
          Upload your first file to get started
        </p>
        <motion.button
          className="rfile-empty-btn"
          onClick={onUpload}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiUploadCloud size={16} />
          Upload Files
        </motion.button>
      </>
    )}
  </motion.div>
);

// Loading State Component
const LoadingState = () => (
  <div className="rfile-loading">
    <motion.div
      className="rfile-loading-spinner"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    />
    <p className="rfile-loading-text">Loading files...</p>
  </div>
);

// ==============================================
// Main Component
// ==============================================
const FilesTab = ({ roomId, userRole }) => {
  const { user } = useContext(UserContext);
  const {
    files,
    filesLoading,
    fetchFiles,
    uploadFile,
    downloadFile,
    deleteFile,
    getFileVersions,
    setCurrentRoomId,
  } = useRoomFiles();

  const [filterType, setFilterType] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedFileForVersions, setSelectedFileForVersions] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const userId = user?.id || user?._id;

  useEffect(() => {
    setCurrentRoomId(roomId);
  }, [roomId, setCurrentRoomId]);

  useEffect(() => {
    if (roomId) {
      fetchFiles(roomId);
    }
  }, [roomId, fetchFiles]);

  const filteredFiles = useMemo(() => {
    if (filterType === "all") return files;
    return files.filter((f) => f.fileType === filterType);
  }, [files, filterType]);

  const getTypeCount = (type) => {
    if (type === "all") return files.length;
    return files.filter((f) => f.fileType === type).length;
  };

  const handleUploadSuccess = async (file, options, onProgress) => {
    try {
      await uploadFile(roomId, file, options, onProgress);
    } catch (error) {
      throw error;
    }
  };

  const handleDelete = async (attachmentId) => {
    try {
      await deleteFile(roomId, attachmentId);
    } catch (error) {
throw error;
    }
  };

  const handleViewVersions = (attachmentId) => {
    setSelectedFileForVersions(attachmentId);
    setShowVersionHistory(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchFiles(roomId);
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const clearFilters = () => {
    setFilterType("all");
  };

  // Loading state
  if (filesLoading && files.length === 0) {
    return (
      <div className="rfile-container">
        <LoadingState />
      </div>
    );
  }

  return (
    <motion.div
      className="rfile-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <FilesHeader
        fileCount={files.length}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onUpload={() => setShowUploadModal(true)}
      />

      {/* Controls Row */}
      <div className="rfile-controls">
        <FilterTabs
          activeType={filterType}
          onTypeChange={setFilterType}
          getTypeCount={getTypeCount}
        />
        <ViewToggle view={viewMode} onViewChange={setViewMode} />
      </div>

      {/* Files Content */}
      <main className="rfile-main">
        {filteredFiles.length === 0 ? (
          <EmptyState
            hasFilters={filterType !== "all"}
            onClearFilters={clearFilters}
            onUpload={() => setShowUploadModal(true)}
          />
        ) : (
          <motion.div
            className={`rfile-grid ${
              viewMode === "list" ? "rfile-grid--list" : ""
            }`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AnimatePresence mode="popLayout">
              {filteredFiles.map((file) => (
                <motion.div
                  key={file._id}
                  variants={itemVariants}
                  layout
                  exit="exit"
                >
                  <FileCard
                    file={file}
                    roomId={roomId}
                    userRole={userRole}
                    currentUserId={userId}
                    viewMode={viewMode}
                    onUpdate={handleRefresh}
                    onDelete={handleDelete}
                    onViewVersions={handleViewVersions}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </main>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <FileUploadModal
            roomId={roomId}
            onClose={() => setShowUploadModal(false)}
            onUploadSuccess={handleUploadSuccess}
          />
        )}
      </AnimatePresence>

      {/* Version History Modal */}
      <AnimatePresence>
        {showVersionHistory && selectedFileForVersions && (
          <FileVersionHistory
            roomId={roomId}
            attachmentId={selectedFileForVersions}
            onClose={() => {
              setShowVersionHistory(false);
              setSelectedFileForVersions(null);
            }}
            onDownload={downloadFile}
            getVersions={getFileVersions}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FilesTab;
